import React from 'react'
import { useTranslation } from '@mc/i18n'
import { useExperiment } from '../../hooks/useExperiment'
import { useTrackExperimentViewed } from '../../hooks/useTrackExperimentViewed'
import { PROMO_BANNER_COUNTDOWN_I18N_NAMESPACE } from '../../i18n'
import { formatCountdown } from '../../utils/formatCountdown'

type CountdownWrapperProps = {
  children: React.ReactNode
  isGift?: boolean
}

const Separator = () => (
  <span className='mc-text-color--primary' aria-hidden='true'>
    {' : '}
  </span>
)

/**
 * Swaps the static urgency copy for a live countdown inside the last 24 hours of a promotion.
 *
 * Self-gating: control, and anyone outside the countdown window, renders the `children` the
 * callsite already had, so callsites stay experiment-agnostic.
 */
export const CountdownWrapper = ({
  children,
  isGift,
}: CountdownWrapperProps) => {
  const { t } = useTranslation(PROMO_BANNER_COUNTDOWN_I18N_NAMESPACE)
  const { isEligible, isVariant, remainingMs } = useExperiment({ isGift })

  useTrackExperimentViewed(isEligible)

  if (!isVariant || remainingMs === null) return <>{children}</>

  const { hours, minutes, seconds } = formatCountdown(remainingMs)

  // tabular-nums keeps every digit the same width. Without it the seconds change the string's
  // width once a second and visibly shift the CTA next to it; no design-system utility covers
  // this. The abbreviations are spelled out in an aria-label so screen readers don't read
  // "HRS" as letters — the element is not a live region, so it is not re-announced on each tick.
  return (
    <span
      className='mc-text--uppercase'
      style={{ fontVariantNumeric: 'tabular-nums' }}
      aria-label={t('countdown.a11y', { hours, minutes, seconds })}
    >
      {t('countdown.endsIn')} {hours} {t('countdown.hours')}
      <Separator />
      {minutes} {t('countdown.minutes')}
      <Separator />
      {seconds} {t('countdown.seconds')}
    </span>
  )
}
